import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import type { AxiosError } from "axios";
import type { ErrorInfo } from "react";

const isDevelopment =
	import.meta.env.DEV || import.meta.env.VITE_CUSTOM_ENV_NAME === "development";

const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
	config: {
		connectionString: import.meta.env
			.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
		extensions: [reactPlugin],
		enableAutoRouteTracking: true,
		autoTrackPageVisitTime: true,
		enableUnhandledPromiseRejectionTracking: true,
	},
});

export function initializeAppInsights(): void {
	const hasConnectionString = !!import.meta.env
		.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;
	const devOverride =
		isDevelopment && import.meta.env.VITE_ENABLE_APP_INSIGHTS === "true";
	const shouldEnable = hasConnectionString && (!isDevelopment || devOverride);

	if (shouldEnable) {
		appInsights.loadAppInsights();
		appInsights.trackPageView();
	}
}

export function setAppInsightsUser(
	userId: string,
	accountId?: string,
	email?: string,
): void {
	appInsights.setAuthenticatedUserContext(userId, accountId, true);

	if (email) {
		appInsights.addTelemetryInitializer((envelope) => {
			if (envelope.data) {
				envelope.data.email = email;
			}
		});
	}
}

export function clearAppInsightsUser(): void {
	appInsights.clearAuthenticatedUserContext();
}

export function trackReactError(
	error: Error,
	errorInfo: ErrorInfo,
	boundaryName = "ErrorBoundary",
): void {
	appInsights.trackException({
		exception: error,
		properties: {
			boundaryName,
			componentStack: errorInfo.componentStack,
			route: window.location.pathname,
		},
	});
}

export function trackAxiosError(error: AxiosError): void {
	const responseStatus = error.response?.status;
	const requestUrl = error.config?.url;
	const baseUrl = error.config?.baseURL;
	const method = error.config?.method?.toUpperCase();

	appInsights.trackException({
		exception:
			error instanceof Error
				? error
				: new Error("Axios request failed without an Error instance"),
		properties: {
			baseUrl,
			method,
			requestUrl,
			responseStatus,
			route: window.location.pathname,
			isNetworkError: error.code === "ERR_NETWORK",
		},
	});
}

export { reactPlugin, appInsights };
